import React, { SyntheticEvent, useCallback } from 'react'
import classnames from 'classnames'
import { useTranslations, useLayout } from '../hooks'
import ProgressBar from '../progress-bar'
import { ProductCardGallerySkeleton, ProductCardSkeleton } from '../skeleton'
import Spinner from '../spinner'
import { useCookieState } from '../hooks/use-cookie-state'

type Props = {
  onClick: () => void
  currentlyViewing: number
  maxViewing: number
  loading: boolean
  nextPagePath?: string
  ensureServerSideNavigation?: boolean
}
export const InfinityScrollButton = ({
  onClick,
  currentlyViewing,
  maxViewing,
  loading,
  nextPagePath = '',
  ensureServerSideNavigation = false,
}: Props) => {
  const { translate } = useTranslations()
  const { responsive } = useLayout()

  const {
    cookies: {
      userSettings: { viewType },
    },
  } = useCookieState()

  const galleryTeaserCards = viewType === 'gallery' || responsive.isSmallScreen

  const handleClick = useCallback(
    (e: SyntheticEvent) => {
      if (!ensureServerSideNavigation) {
        e.preventDefault()
        onClick()
      }
    },
    [onClick, ensureServerSideNavigation]
  )

  const withTeaserCards = currentlyViewing < maxViewing

  const shouldShowMoreButton = currentlyViewing < maxViewing

  return (
    <div
      className={classnames({
        'c-infinity-scroll-button u-relative u-overflow-hidden d-flex align-items-end justify-content-center':
          withTeaserCards,
      })}
      style={withTeaserCards ? { height: galleryTeaserCards ? 300 : 220 } : undefined}>
      {withTeaserCards && (
        <div className="u-absolute-0" id="infinity-scroll-with-teaser">
          <div className="row sm-gutters" style={{ opacity: 1 }}>
            {galleryTeaserCards ? (
              <>
                <div className="col-12 col-sm-6 col-md-4">
                  <ProductCardGallerySkeleton noAnimation={!loading} />
                </div>
                <div className="col-12 col-sm-6 col-md-4">
                  <ProductCardGallerySkeleton noAnimation={!loading} />
                </div>
                <div className="col-12 col-sm-6 col-md-4">
                  <ProductCardGallerySkeleton noAnimation={!loading} />
                </div>
              </>
            ) : (
              <div className="col-12">
                <ProductCardSkeleton noAnimation={!loading} />
              </div>
            )}
          </div>
          <div
            className="u-absolute-0"
            style={{
              background: 'linear-gradient(to bottom, transparent, #f0f0f0 50%)',
            }}></div>
        </div>
      )}
      <div className="u-relative" style={{ zIndex: 1 }}>
        <div className="d-flex justify-content-center u-mb-8 u-relative">
          {loading && !withTeaserCards && <Spinner local size="xs"></Spinner>}
          <div className={classnames({ 'u-disabled': loading })}>
            <p className="u-mb-16 u-px-24 u-text-center">
              {translate('InfinityScroll_ResultText', {
                currentProductCount: currentlyViewing,
                totalProductCount: maxViewing,
              })}
            </p>
            <ProgressBar
              className="u-mb-16"
              value={currentlyViewing}
              maxValue={maxViewing}
              style={{ minWidth: 250 }}
            />
          </div>
        </div>
        <div className="d-flex justify-content-center u-mb-16">
          {shouldShowMoreButton && nextPagePath.length > 0 && (
            <a
              href={nextPagePath}
              className={classnames('c-btn', 'u-mb-0', { 'u-disabled': loading })}
              onClick={handleClick}>
              {translate('ShowMore')}
            </a>
          )}
          {shouldShowMoreButton && nextPagePath.length === 0 && (
            <button
              className="c-infinity-scroll-button__button c-btn u-mb-0"
              onClick={onClick}
              disabled={loading}>
              {translate('ShowMore')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
export default InfinityScrollButton
